import { useState, useEffect } from "react";
import { listApplications } from "../services/applications.js";

const STATUS_LABELS = {
  pending_details: { label: "Pending Details", color: "#94a3b8", bg: "#1e2d45" },
  pending_verification: { label: "Pending Verification", color: "#f59e0b", bg: "#422006" },
  pending_approval: { label: "Awaiting Review", color: "#3b82f6", bg: "#1e3a5f" },
  approved: { label: "Approved", color: "#10b981", bg: "#064e3b" },
  rejected: { label: "Rejected", color: "#ef4444", bg: "#450a0a" },
  invited: { label: "Invited", color: "#8b5cf6", bg: "#2e1065" },
};

const FILTERS = [
  { value: "", label: "All" },
  { value: "pending_approval", label: "Awaiting Review" },
  { value: "pending_details", label: "Pending Details" },
  { value: "pending_verification", label: "Pending Docs" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "invited", label: "Invited" },
];

export function ApplicationsList({ onSelect }) {
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const LIMIT = 20;

  useEffect(() => {
    setLoading(true);
    listApplications({ status: filter || undefined, limit: LIMIT, offset })
      .then((data) => {
        setApps(data.applications ?? []);
        setTotal(data.total ?? 0);
      })
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
  }, [filter, offset]);

  const st = (status) => STATUS_LABELS[status] || { label: status, color: "#94a3b8", bg: "#1e2d45" };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>
          Admin
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif", margin: 0 }}>
          Applications
        </h1>
        <p style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
          {total} total application{total !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => { setFilter(f.value); setOffset(0); }}
            style={{
              padding: "6px 14px", borderRadius: 20, border: "none",
              background: filter === f.value ? "#1e3a5f" : "#0d1724",
              color: filter === f.value ? "#60a5fa" : "#64748b",
              fontSize: 12, fontWeight: filter === f.value ? 700 : 400,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ color: "#64748b", textAlign: "center", padding: 40 }}>Loading...</div>
      ) : apps.length === 0 ? (
        <div style={{ color: "#64748b", textAlign: "center", padding: 40, background: "#0d1724", borderRadius: 12, border: "1px solid #1e2d45" }}>
          No applications{filter ? ` with status "${FILTERS.find(f => f.value === filter)?.label}"` : ""}.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {apps.map((app) => {
            const s = st(app.status);
            return (
              <button
                key={app.id}
                onClick={() => onSelect?.(app)}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "14px 18px", background: "#0d1724", border: "1px solid #1e2d45",
                  borderRadius: 12, cursor: "pointer", textAlign: "left", width: "100%",
                  fontFamily: "inherit", transition: "border-color 0.15s",
                }}
              >
                {/* Avatar circle */}
                <div style={{
                  width: 40, height: 40, borderRadius: "50%", background: "#1e3a5f",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontWeight: 700, color: "#60a5fa", flexShrink: 0,
                }}>
                  {(app.officialName || app.email || "?").charAt(0).toUpperCase()}
                </div>

                {/* Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {app.officialName || app.email || "Unnamed"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {app.electoralPosition || "No position"} · {app.email}
                  </div>
                  {app.createdAt && (
                    <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>
                      Applied {new Date(app.createdAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Status badge */}
                <div style={{
                  padding: "4px 10px", borderRadius: 12,
                  background: s.bg, color: s.color,
                  fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
                }}>
                  {s.label}
                </div>

                {/* Arrow */}
                <span style={{ color: "#475569", fontSize: 16 }}>→</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 20 }}>
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            style={{ padding: "8px 16px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 8, color: offset === 0 ? "#2d3f5a" : "#94a3b8", cursor: offset === 0 ? "default" : "pointer", fontFamily: "inherit", fontSize: 13 }}
          >
            ← Previous
          </button>
          <span style={{ color: "#64748b", fontSize: 13, padding: "8px 0" }}>
            {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <button
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
            style={{ padding: "8px 16px", background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 8, color: offset + LIMIT >= total ? "#2d3f5a" : "#94a3b8", cursor: offset + LIMIT >= total ? "default" : "pointer", fontFamily: "inherit", fontSize: 13 }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default ApplicationsList;
